'use client';

import { useEffect, useRef, useState } from 'react';
import { textToAnchor } from '@portal/content-renderer';

function TableOfContentsInternal({
    items,
    hasActiveIndicator,
}: {
    items: { slug: string; text: string; indent: number }[];
    hasActiveIndicator: boolean;
}) {
    const [activeSlug, setActiveSlug] = useState<string | null>(null);
    const anchors = useRef<HTMLElement[]>([]);
    useEffect(() => {
        if (items && items.length) {
            const headings: HTMLElement[] = items.reduce((current, item) => {
                const elem = document.getElementById(item.slug);
                return elem ? [...current, elem] : current;
            }, []);
            anchors.current = headings;
        }
    }, [items]);

    useEffect(() => {
        if (hasActiveIndicator) {
            const onScroll = () => {
                const slug =
                    anchors.current.find((item) => item.offsetTop > (document.scrollingElement?.scrollTop || 0))?.id ||
                    null;
                setActiveSlug(slug);
            };

            document.addEventListener('scroll', onScroll);
            document.addEventListener('resize', onScroll);

            onScroll();

            return () => {
                document.removeEventListener('scroll', onScroll);
                document.removeEventListener('resize', onScroll);
            };
        }
    }, [hasActiveIndicator]);

    return (
        <ul className="text-sm">
            {items.map((item) => {
                let className: string | undefined = undefined;
                switch (item.indent) {
                    case 1:
                        className = 'pl-4';
                        break;
                    case 2:
                        className = 'pl-8';
                        break;
                    case 3:
                        className = 'pl-12';
                        break;
                }
                return (
                    <li
                        className={`relative py-1 ${className}${
                            activeSlug === item.slug
                                ? " font-bold before:absolute before:-ml-4 before:content-['›']"
                                : ''
                        }`}
                        key={item.slug}>
                        <a className="text-primary hover:underline" href={`#${item.slug}`}>
                            {item.text}
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

export default function TableOfContents({
    document,
    ignoreH1 = false,
    hasActiveIndicator = true,
}: {
    document: any;
    ignoreH1?: boolean;
    hasActiveIndicator?: boolean;
}) {
    const headings = document?.filter(
        (item) => item.type === 'heading' && item.children?.[0]?.text && (!ignoreH1 || item.level !== 1),
    );
    const minLevel = headings.reduce((current, item) => (item.level < current ? item.level : current), 10);

    const items: any[] = headings
        .filter((item) => item.level === minLevel)
        .map((item) => {
            const text = item.children[0].text;
            return { slug: textToAnchor(text), text, indent: item.level - minLevel };
        });

    if (!items?.length) {
        return null;
    }

    return (
        <>
            <h2 className={`${hasActiveIndicator ? 'mb-4 ' : ''}text-xl`}>Cuprins articol</h2>
            <TableOfContentsInternal items={items} hasActiveIndicator={hasActiveIndicator} />
        </>
    );
}
